import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, FileDown, Loader2, Plus, Save, ShieldAlert, Trash2,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  ADJUSTMENT_OPTIONS, ASSESSMENT_STATUS_LABELS, COMMUNICATION_METHODS, CONCERN_CATEGORIES,
  GROUPS, OBSERVED_JOURNEY_ITEMS, RECOMMENDATIONS, RESPONSE_SCALE, SAFETY_CRITICAL,
  SENSORY_TRIGGERS, STRENGTH_OPTIONS, TRAINING_NEED_AREAS, TRANSPORT_TYPES,
} from "@/config/assessmentCatalog";

const LOCKED = ["awaiting_manager_review", "approved", "returned_for_amendment", "cancelled", "completed_by_assessor"];
const COMMENT_REQUIRED = ["with_support", "not_yet_demonstrated"];

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-600 border-transparent",
  in_progress: "bg-sky-100 text-sky-800 border-transparent",
  awaiting_information: "bg-amber-100 text-amber-800 border-transparent",
  completed_by_assessor: "bg-indigo-100 text-indigo-800 border-transparent",
  awaiting_manager_review: "bg-indigo-100 text-indigo-800 border-transparent",
  provisional: "bg-amber-100 text-amber-800 border-transparent",
  approved: "bg-ukgreen-light text-ukgreen border-transparent",
  returned_for_amendment: "bg-orange-100 text-orange-800 border-transparent",
  cancelled: "bg-slate-100 text-slate-500 border-transparent",
};

const EMPTY_CONTENT = {
  responses: {}, scenarios: {}, sensory_issues: [], observed_journey: { items: {}, transport_types: [] },
  text_sections: { m: {}, n: {}, o: {} }, strengths: { selected: [], other: "" },
  training_needs: [], adjustments: { selected: [], custom: "" }, safety_concerns: [],
  summary: {}, communication_methods: [], comm_adjustments: "",
};

function ScaleItem({ item, value, onChange, readOnly }) {
  const r = value || {};
  const needsComment = SAFETY_CRITICAL.has(item.key) && COMMENT_REQUIRED.includes(r.response);
  return (
    <div className={`rounded-md border p-3 space-y-2 ${needsComment && !r.comment?.trim() ? "border-red-300 bg-red-50/50" : "border-border"}`}
      data-testid={`item-${item.key}`}>
      <p className="text-sm font-medium text-slate-800">
        {item.label}
        {SAFETY_CRITICAL.has(item.key) && <span className="ml-1 text-[10px] uppercase tracking-wide text-red-600 font-bold">safety-critical</span>}
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5">
        {RESPONSE_SCALE.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={readOnly}
            data-testid={`resp-${item.key}-${opt.value}`}
            onClick={() => onChange({ ...r, response: opt.value })}
            className={`rounded-md px-2 py-2.5 text-[11px] font-medium leading-tight transition-colors duration-150 min-h-[44px] ${
              r.response === opt.value ? "bg-primary text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
            } ${readOnly ? "cursor-not-allowed opacity-80" : ""}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <Textarea
        rows={needsComment ? 2 : 1}
        placeholder={needsComment ? "Comment required — significant support needed or skill not demonstrated" : "Assessor comment (optional)"}
        value={r.comment || ""}
        disabled={readOnly}
        data-testid={`comment-${item.key}`}
        onChange={(e) => onChange({ ...r, comment: e.target.value })}
        className="text-sm"
      />
    </div>
  );
}

export default function AssessmentPage() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [assessment, setAssessment] = useState(null);
  const [denied, setDenied] = useState(false);
  const [groupIdx, setGroupIdx] = useState(0);
  const [header, setHeader] = useState({});
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [recommendation, setRecommendation] = useState("");
  const [recReason, setRecReason] = useState("");
  const [concern, setConcern] = useState({ active: false, description: "", observed_behaviour: "", immediate_control: "", recommended_action: "" });
  const [dirty, setDirty] = useState(false);
  const [saveState, setSaveState] = useState("");
  const [missing, setMissing] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [reviewDecision, setReviewDecision] = useState("approve");
  const [reviewComments, setReviewComments] = useState("");
  const [versions, setVersions] = useState([]);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/assessments/${assessmentId}`);
      setAssessment(data);
      setHeader({
        scheduled_date: data.scheduled_date || "", assessment_date: data.assessment_date || "",
        start_time: data.start_time || "", end_time: data.end_time || "", location: data.location || "",
        parent_present: !!data.parent_present, school_rep_present: !!data.school_rep_present,
        others_present: data.others_present || "",
      });
      setContent({ ...EMPTY_CONTENT, ...(data.content || {}) });
      setRecommendation(data.assessor_recommendation || "");
      setRecReason(data.assessor_recommendation_reason || "");
      setConcern({ active: false, description: "", observed_behaviour: "", immediate_control: "", recommended_action: "", ...(data.road_safety_concern || {}) });
      const v = await api.get(`/assessments/${assessmentId}/versions`).catch(() => null);
      if (v) setVersions(v.data.items);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [assessmentId]);

  useEffect(() => {
    load();
  }, [load]);

  const editable = assessment && !LOCKED.includes(assessment.status)
    && (assessment.assessor_id === user?.id || (assessment.status === "provisional" && user?.role === "travel_trainer"));

  const save = useCallback(async (silent = true) => {
    if (!editable) return;
    setSaveState("saving");
    try {
      await api.patch(`/assessments/${assessmentId}`, {
        content, ...header,
        assessor_recommendation: recommendation || null,
        assessor_recommendation_reason: recReason || null,
        road_safety_concern: concern,
        status: assessment.status === "draft" ? "in_progress" : undefined,
      });
      setDirty(false);
      setSaveState("saved");
    } catch (err) {
      setSaveState("error");
      if (!silent) toast.error(formatApiError(err));
    }
  }, [editable, assessmentId, content, header, recommendation, recReason, concern, assessment?.status]);

  useEffect(() => {
    if (!dirty || !editable) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => save(true), 1500);
    return () => clearTimeout(saveTimer.current);
  }, [dirty, content, header, recommendation, recReason, concern, editable, save]);

  const update = (fn) => {
    setContent((c) => {
      const next = typeof fn === "function" ? fn(c) : fn;
      return next;
    });
    setDirty(true);
  };
  const setResponse = (key, val) => update((c) => ({ ...c, responses: { ...c.responses, [key]: val } }));
  const setHeaderField = (k, v) => { setHeader((h) => ({ ...h, [k]: v })); setDirty(true); };

  const clientCompleteness = () => {
    const m = [];
    if (!header.assessment_date) m.push("Assessment date (Details)");
    if (!header.location?.trim()) m.push("Assessment location (Details)");
    const s = content.summary || {};
    if (!s.key_strengths?.trim()) m.push("Summary: key strengths");
    if (!s.key_concerns?.trim()) m.push("Summary: key concerns");
    if (!s.training_needs_summary?.trim()) m.push("Summary: training needs");
    if (!s.recommended_approach?.trim()) m.push("Summary: recommended training approach");
    if (!recommendation) m.push("Assessor recommendation");
    if (!recReason.trim()) m.push("Reason for recommendation");
    Object.entries(content.responses || {}).forEach(([key, r]) => {
      if (SAFETY_CRITICAL.has(key) && COMMENT_REQUIRED.includes(r?.response) && !r?.comment?.trim()) {
        m.push(`Comment required: ${key.replace(/_/g, " ")}`);
      }
    });
    if (concern.active) {
      if (!concern.description?.trim()) m.push("Road safety concern: description");
      if (!concern.observed_behaviour?.trim()) m.push("Road safety concern: observed behaviour");
      if (!concern.immediate_control?.trim()) m.push("Road safety concern: immediate control/mitigation");
      if (!concern.recommended_action?.trim()) m.push("Road safety concern: recommended action");
    }
    return m;
  };

  const submit = async () => {
    const m = clientCompleteness();
    if (m.length) {
      setMissing(m);
      toast.error("Assessment cannot yet be submitted — see required areas");
      return;
    }
    setMissing([]);
    setSubmitting(true);
    try {
      await save(true);
      await api.post(`/assessments/${assessmentId}/submit`);
      toast.success("Assessment submitted for manager review");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        setMissing(detail.missing);
        toast.error(detail.message);
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const review = async (decisionOverride) => {
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/review`, { decision: decisionOverride || reviewDecision, comments: reviewComments || null });
      toast.success(data.case_status ? `Review recorded — case status: ${data.case_status.replace(/_/g, " ")}` : "Review recorded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const amend = async () => {
    const reason = window.prompt("Reason for amendment (required, recorded in audit history):");
    if (!reason?.trim()) return;
    try {
      const { data } = await api.post(`/assessments/${assessmentId}/amend`, { reason });
      toast.success(`Amendment created as version ${data.version}`);
      navigate(`/assessments/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (denied) {
    return (
      <div data-testid="assessment-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mb-6">
          <ShieldAlert className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-base text-slate-600 max-w-md mb-8">This assessment does not exist, or you are not authorised to view it. This attempt has been recorded.</p>
        <Link to="/dashboard" className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-white hover:bg-brand-hover">Back to dashboard</Link>
      </div>
    );
  }

  if (!assessment) {
    return <div className="flex items-center gap-2 text-slate-600" data-testid="assessment-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading assessment…</div>;
  }

  const group = GROUPS[groupIdx];
  const canReview = hasPermission("assessments:review") && assessment.status === "awaiting_manager_review"
    && (assessment.assessor_id !== user?.id || user?.role === "super_admin");
  const canAmend = ["returned_for_amendment", "awaiting_information", "approved"].includes(assessment.status)
    && (assessment.assessor_id === user?.id || hasPermission("assessments:review"));
  const canProvisional = hasPermission("assessments:review")
    && ["draft", "in_progress", "awaiting_information"].includes(assessment.status);

  const renderScaleItems = (items) => (
    <div className="space-y-3">
      {items.map((it) => (
        <ScaleItem key={it.key} item={it} value={content.responses[it.key]} onChange={(v) => setResponse(it.key, v)} readOnly={!editable} />
      ))}
    </div>
  );

  return (
    <div data-testid="assessment-page" className="space-y-5 max-w-5xl mx-auto">
      <Link to={`/children/${assessment.child_id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary" data-testid="back-to-child">
        <ArrowLeft className="h-4 w-4" /> Back to {assessment.child?.child_name}
      </Link>
      {/* Header */}
      <div className="rounded-lg border border-border bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900" data-testid="assessment-number">
                {assessment.assessment_number} <span className="text-base font-semibold text-slate-500">v{assessment.version}</span>
              </h1>
              <Badge className={STATUS_STYLES[assessment.status]} data-testid="assessment-status">
                {ASSESSMENT_STATUS_LABELS[assessment.status]}
              </Badge>
              {editable && <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="assessment-draft-badge">DRAFT — not submitted</Badge>}
            </div>
            <p className="text-sm text-slate-600 mt-1" data-testid="assessment-child-brief">
              {assessment.child?.child_name} · {assessment.child?.case_id} · {assessment.child?.school_name || "No school"} · {assessment.child?.council_name || "No local authority"}
              {assessment.child?.council_reference ? ` · Ref ${assessment.child.council_reference}` : ""}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Assessor: {assessment.assessor_name} · Created {new Date(assessment.created_at).toLocaleString("en-GB")}
              {assessment.submitted_at ? ` · Submitted ${new Date(assessment.submitted_at).toLocaleString("en-GB")}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {editable && (
              <span className="text-xs text-slate-500" data-testid="autosave-status">
                {saveState === "saving" ? "Saving…" : saveState === "saved" ? "All changes saved" : dirty ? "Unsaved changes" : ""}
              </span>
            )}
            {editable && (
              <Button variant="outline" className="h-10" data-testid="save-draft-button" onClick={() => save(false)}>
                <Save className="h-4 w-4 mr-2" /> Save draft
              </Button>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/assessments/${assessmentId}/pdf`} target="_blank" rel="noreferrer"
              data-testid="assessment-pdf-button"
              className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-medium text-primary hover:bg-muted">
              <FileDown className="h-4 w-4 mr-2" /> PDF report
            </a>
            {canAmend && (
              <Button variant="outline" className="h-10" data-testid="assessment-amend-button" onClick={amend}>Create amendment</Button>
            )}
          </div>
        </div>
        {assessment.amendment_reason && (
          <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2" data-testid="amendment-reason">
            Amendment reason: {assessment.amendment_reason}
          </p>
        )}
        {assessment.manager_review && (
          <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="manager-review-summary">
            <span className="font-semibold">Manager decision: {assessment.manager_review.decision.replace(/_/g, " ")}</span>
            <span className="text-slate-600"> by {assessment.manager_review.manager_name} · {(assessment.manager_review.decided_at || "").slice(0, 16).replace("T", " ")}</span>
            {assessment.manager_review.comments && <p className="text-xs text-slate-600 mt-1">{assessment.manager_review.comments}</p>}
          </div>
        )}
        {versions.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="version-history">
            {versions.map((v) => (
              <Link key={v.id} to={`/assessments/${v.id}`} data-testid={`version-link-${v.version}`}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${v.id === assessmentId ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                v{v.version} · {ASSESSMENT_STATUS_LABELS[v.status]}
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Section nav */}
      <ol className="flex flex-wrap gap-1.5" data-testid="assessment-sections">
        {GROUPS.map((g, i) => (
          <li key={g.id}>
            <button type="button" data-testid={`section-${g.id}`} onClick={() => setGroupIdx(i)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 min-h-[44px] ${
                i === groupIdx ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}>
              {g.num}. {g.title}
            </button>
          </li>
        ))}
      </ol>

      {missing.length > 0 && (
        <div className="rounded-md border border-red-300 bg-red-50 p-4" data-testid="completeness-errors" role="alert">
          <p className="text-sm font-semibold text-red-800">Assessment cannot yet be submitted. The following required areas need attention:</p>
          <ul className="mt-2 list-disc pl-5 text-sm text-red-800 space-y-0.5">
            {missing.map((m, i) => <li key={i} data-testid={`missing-${i}`}>{m}</li>)}
          </ul>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{group.title}</CardTitle>
          {group.intro && <p className="text-xs text-slate-500">{group.intro}</p>}
        </CardHeader>
        <CardContent className="space-y-4">
          {group.type === "details" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="assessment-details-form">
              <div className="space-y-1.5"><Label>Scheduled date</Label>
                <Input type="date" data-testid="field-scheduled-date" value={header.scheduled_date} disabled={!editable} onChange={(e) => setHeaderField("scheduled_date", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Assessment date *</Label>
                <Input type="date" data-testid="field-assessment-date" value={header.assessment_date} disabled={!editable} onChange={(e) => setHeaderField("assessment_date", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Start time</Label>
                <Input type="time" data-testid="field-start-time" value={header.start_time} disabled={!editable} onChange={(e) => setHeaderField("start_time", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>End time</Label>
                <Input type="time" data-testid="field-end-time" value={header.end_time} disabled={!editable} onChange={(e) => setHeaderField("end_time", e.target.value)} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Assessment location *</Label>
                <Input data-testid="field-location" value={header.location} disabled={!editable} onChange={(e) => setHeaderField("location", e.target.value)} placeholder="e.g. Family home / school / accompanied journey" /></div>
              <label className="flex items-center gap-2 min-h-[44px] text-sm">
                <Checkbox data-testid="field-parent-present" checked={header.parent_present} disabled={!editable} onCheckedChange={(v) => setHeaderField("parent_present", Boolean(v))} />
                Parent/carer present
              </label>
              <label className="flex items-center gap-2 min-h-[44px] text-sm">
                <Checkbox data-testid="field-school-rep" checked={header.school_rep_present} disabled={!editable} onCheckedChange={(v) => setHeaderField("school_rep_present", Boolean(v))} />
                School representative present
              </label>
              <div className="space-y-1.5 sm:col-span-2"><Label>Other people present</Label>
                <Input data-testid="field-others-present" value={header.others_present} disabled={!editable} onChange={(e) => setHeaderField("others_present", e.target.value)} /></div>
              <p className="text-xs text-slate-500 sm:col-span-2">The assessor is always recorded as the signed-in user and cannot be reassigned on the form.</p>
            </div>
          )}

          {group.type === "scale" && (
            <>
              {renderScaleItems(group.items)}
              {group.id === "b" && (
                <div className="rounded-md border border-border p-4 space-y-3" data-testid="communication-methods">
                  <Label>Communication methods used</Label>
                  <div className="flex flex-wrap gap-3">
                    {COMMUNICATION_METHODS.map((m) => (
                      <label key={m} className="flex items-center gap-2 text-sm min-h-[44px]">
                        <Checkbox data-testid={`comm-method-${m.replace(/\s+/g, "-").toLowerCase()}`}
                          checked={(content.communication_methods || []).includes(m)}
                          disabled={!editable}
                          onCheckedChange={(v) => update((c) => ({ ...c, communication_methods: v ? [...(c.communication_methods || []), m] : (c.communication_methods || []).filter((x) => x !== m) }))} />
                        {m}
                      </label>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Reasonable adjustments required</Label>
                    <Textarea rows={2} data-testid="comm-adjustments" value={content.comm_adjustments || ""} disabled={!editable}
                      onChange={(e) => update((c) => ({ ...c, comm_adjustments: e.target.value }))} />
                  </div>
                </div>
              )}
              {group.concern && (
                <div className={`rounded-md border-2 p-4 space-y-3 ${concern.active ? "border-red-400 bg-red-50" : "border-dashed border-slate-300"}`} data-testid="road-safety-concern">
                  <label className="flex items-center gap-2 text-sm font-bold text-red-700 min-h-[44px]">
                    <Checkbox data-testid="road-concern-toggle" checked={concern.active} disabled={!editable}
                      onCheckedChange={(v) => { setConcern((c) => ({ ...c, active: Boolean(v) })); setDirty(true); }} />
                    <AlertTriangle className="h-4 w-4" /> ROAD SAFETY CONCERN
                  </label>
                  {concern.active && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[["description", "Concern description *"], ["observed_behaviour", "Observed behaviour *"],
                        ["immediate_control", "Immediate control/mitigation *"], ["recommended_action", "Recommended action *"]].map(([k, label]) => (
                        <div key={k} className="space-y-1.5">
                          <Label>{label}</Label>
                          <Textarea rows={2} data-testid={`road-concern-${k}`} value={concern[k]} disabled={!editable}
                            onChange={(e) => { setConcern((c) => ({ ...c, [k]: e.target.value })); setDirty(true); }} />
                        </div>
                      ))}
                      <p className="text-xs text-red-700 sm:col-span-2">This flag does not automatically determine suitability — it is escalated for assessor/manager review.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {group.type === "transport" && (
            <>
              <div className="rounded-md border border-border p-4 space-y-2" data-testid="transport-types">
                <Label>Transport types relevant to this journey</Label>
                <div className="flex flex-wrap gap-3">
                  {TRANSPORT_TYPES.map((t) => (
                    <label key={t} className="flex items-center gap-2 text-sm min-h-[44px]">
                      <Checkbox data-testid={`transport-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        checked={(content.observed_journey.transport_types || []).includes(t)}
                        disabled={!editable}
                        onCheckedChange={(v) => update((c) => ({ ...c, observed_journey: { ...c.observed_journey, transport_types: v ? [...(c.observed_journey.transport_types || []), t] : (c.observed_journey.transport_types || []).filter((x) => x !== t) } }))} />
                      {t}
                    </label>
                  ))}
                </div>
              </div>
              <h3 className="font-semibold text-slate-800 text-sm">Public transport skills</h3>
              {renderScaleItems(group.transportItems)}
              <h3 className="font-semibold text-slate-800 text-sm pt-2">Oyster / ticket / travel card skills <span className="font-normal text-slate-500">(Not applicable is allowed)</span></h3>
              {renderScaleItems(group.cardItems)}
            </>
          )}

          {group.type === "scenarios" && (
            <div className="space-y-4" data-testid="scenarios-section">
              {group.scenarios.map((s, i) => {
                const key = `g${i + 1}`;
                const val = content.scenarios[key] || {};
                return (
                  <div key={key} className="rounded-md border border-border p-4 space-y-3" data-testid={`scenario-${key}`}>
                    <p className="text-sm font-semibold text-slate-900">Scenario {i + 1}: {s}</p>
                    <Textarea rows={2} placeholder="Child's response — what would they do?" value={val.response || ""} disabled={!editable}
                      data-testid={`scenario-response-${key}`}
                      onChange={(e) => update((c) => ({ ...c, scenarios: { ...c.scenarios, [key]: { ...val, response: e.target.value } } }))} />
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="space-y-1.5">
                        <Label className="text-xs">Level of understanding</Label>
                        <Select value={val.understanding || ""} disabled={!editable}
                          onValueChange={(v) => update((c) => ({ ...c, scenarios: { ...c.scenarios, [key]: { ...val, understanding: v } } }))}>
                          <SelectTrigger data-testid={`scenario-understanding-${key}`} className="h-10"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="good">Good</SelectItem>
                            <SelectItem value="partial">Partial</SelectItem>
                            <SelectItem value="limited">Limited</SelectItem>
                            <SelectItem value="not_assessed">Not assessed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5"><Label className="text-xs">Concerns</Label>
                        <Input value={val.concerns || ""} disabled={!editable} data-testid={`scenario-concerns-${key}`}
                          onChange={(e) => update((c) => ({ ...c, scenarios: { ...c.scenarios, [key]: { ...val, concerns: e.target.value } } }))} /></div>
                      <div className="space-y-1.5"><Label className="text-xs">Training required</Label>
                        <Input value={val.training_required || ""} disabled={!editable} data-testid={`scenario-training-${key}`}
                          onChange={(e) => update((c) => ({ ...c, scenarios: { ...c.scenarios, [key]: { ...val, training_required: e.target.value } } }))} /></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {group.type === "functional" && (
            <>
              <h3 className="font-semibold text-slate-800 text-sm">H — Attention, memory &amp; route learning</h3>
              {renderScaleItems(group.routeItems)}
              <h3 className="font-semibold text-slate-800 text-sm pt-2">I — Mobility &amp; physical access <span className="font-normal text-slate-500">(travel-relevant only; not a medical diagnosis)</span></h3>
              {renderScaleItems(group.mobilityItems)}
              <h3 className="font-semibold text-slate-800 text-sm pt-2">J — Sensory &amp; environmental factors</h3>
              <div className="space-y-2" data-testid="sensory-issues">
                {(content.sensory_issues || []).map((issue, i) => (
                  <div key={i} className="rounded-md border border-border p-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`sensory-issue-${i}`}>
                    <Select value={issue.trigger || ""} disabled={!editable}
                      onValueChange={(v) => update((c) => ({ ...c, sensory_issues: c.sensory_issues.map((x, j) => j === i ? { ...x, trigger: v } : x) }))}>
                      <SelectTrigger data-testid={`sensory-trigger-${i}`} className="h-10"><SelectValue placeholder="Trigger / issue" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {SENSORY_TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input placeholder="Observed/reported effect" value={issue.effect || ""} disabled={!editable} data-testid={`sensory-effect-${i}`}
                      onChange={(e) => update((c) => ({ ...c, sensory_issues: c.sensory_issues.map((x, j) => j === i ? { ...x, effect: e.target.value } : x) }))} />
                    <Input placeholder="Support strategy" value={issue.strategy || ""} disabled={!editable} data-testid={`sensory-strategy-${i}`}
                      onChange={(e) => update((c) => ({ ...c, sensory_issues: c.sensory_issues.map((x, j) => j === i ? { ...x, strategy: e.target.value } : x) }))} />
                    <div className="flex gap-2">
                      <Input placeholder="Reasonable adjustment" value={issue.adjustment || ""} disabled={!editable} data-testid={`sensory-adjustment-${i}`}
                        onChange={(e) => update((c) => ({ ...c, sensory_issues: c.sensory_issues.map((x, j) => j === i ? { ...x, adjustment: e.target.value } : x) }))} />
                      {!readOnlyGuard(editable) && (
                        <Button type="button" variant="ghost" className="h-10 text-red-600" data-testid={`sensory-remove-${i}`}
                          onClick={() => update((c) => ({ ...c, sensory_issues: c.sensory_issues.filter((_, j) => j !== i) }))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {editable && (
                  <Button type="button" variant="outline" className="h-10" data-testid="sensory-add-button"
                    onClick={() => update((c) => ({ ...c, sensory_issues: [...(c.sensory_issues || []), { trigger: "", effect: "", strategy: "", adjustment: "" }] }))}>
                    <Plus className="h-4 w-4 mr-2" /> Add sensory/environmental factor
                  </Button>
                )}
              </div>
              <h3 className="font-semibold text-slate-800 text-sm pt-2">K — Behaviour &amp; emotional regulation relevant to travel <span className="font-normal text-slate-500">(factual, observable descriptions only)</span></h3>
              {renderScaleItems(group.behaviourItems)}
              <h3 className="font-semibold text-slate-800 text-sm pt-2">L — Digital / mobile phone skills <span className="font-normal text-slate-500">(smartphone ownership is not a suitability requirement)</span></h3>
              {renderScaleItems(group.digitalItems)}
            </>
          )}

          {group.type === "text" && (
            <>
              {Object.entries(group.sections).map(([sec, def]) => (
                <div key={sec} className="space-y-3" data-testid={`text-section-${sec}`}>
                  <h3 className="font-semibold text-slate-800 text-sm">{def.title}</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {def.fields.map(([key, label]) => (
                      <div key={key} className="space-y-1.5">
                        <Label className="text-xs">{label}</Label>
                        <Textarea rows={2} value={content.text_sections?.[sec]?.[key] || ""} disabled={!editable}
                          data-testid={`text-${key}`}
                          onChange={(e) => update((c) => ({ ...c, text_sections: { ...c.text_sections, [sec]: { ...(c.text_sections?.[sec] || {}), [key]: e.target.value } } }))} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {group.type === "observed" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="observed-meta">
                {[["date", "Date", "date"], ["start_location", "Start location", "text"], ["destination", "Destination", "text"],
                  ["route", "Route / direction", "text"], ["start_time", "Start time", "time"], ["end_time", "End time", "time"]].map(([k, label, type]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Input type={type} value={content.observed_journey[k] || ""} disabled={!editable} data-testid={`oj-${k}`}
                      onChange={(e) => update((c) => ({ ...c, observed_journey: { ...c.observed_journey, [k]: e.target.value } }))} />
                  </div>
                ))}
                <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
                  <Label className="text-xs">Weather / environmental factors</Label>
                  <Input value={content.observed_journey.environment || ""} disabled={!editable} data-testid="oj-environment"
                    onChange={(e) => update((c) => ({ ...c, observed_journey: { ...c.observed_journey, environment: e.target.value } }))} />
                </div>
              </div>
              <div className="space-y-3">
                {OBSERVED_JOURNEY_ITEMS.map((it) => (
                  <ScaleItem key={it.key} item={it}
                    value={content.observed_journey.items?.[it.key]}
                    onChange={(v) => update((c) => ({ ...c, observed_journey: { ...c.observed_journey, items: { ...(c.observed_journey.items || {}), [it.key]: v } } }))}
                    readOnly={!editable} />
                ))}
              </div>
            </>
          )}

          {group.type === "needs" && (
            <>
              <div className="rounded-md border border-border p-4 space-y-3" data-testid="strengths-section">
                <h3 className="font-semibold text-slate-800 text-sm">Young person's strengths</h3>
                <div className="flex flex-wrap gap-3">
                  {STRENGTH_OPTIONS.map((s) => (
                    <label key={s} className="flex items-center gap-2 text-sm min-h-[44px]">
                      <Checkbox data-testid={`strength-${s}`} checked={(content.strengths.selected || []).includes(s)} disabled={!editable}
                        onCheckedChange={(v) => update((c) => ({ ...c, strengths: { ...c.strengths, selected: v ? [...c.strengths.selected, s] : c.strengths.selected.filter((x) => x !== s) } }))} />
                      {s.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
                <Textarea rows={2} placeholder="Other observed strengths (do not auto-generate statements)" value={content.strengths.other || ""} disabled={!editable}
                  data-testid="strengths-other"
                  onChange={(e) => update((c) => ({ ...c, strengths: { ...c.strengths, other: e.target.value } }))} />
              </div>

              <div className="rounded-md border border-border p-4 space-y-3" data-testid="training-needs-section">
                <h3 className="font-semibold text-slate-800 text-sm">Identified training needs</h3>
                {(content.training_needs || []).map((n, i) => (
                  <div key={i} className="rounded-md border border-border p-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`training-need-${i}`}>
                    <Select value={n.area || ""} disabled={!editable}
                      onValueChange={(v) => update((c) => ({ ...c, training_needs: c.training_needs.map((x, j) => j === i ? { ...x, area: v } : x) }))}>
                      <SelectTrigger data-testid={`need-area-${i}`} className="h-10"><SelectValue placeholder="Area" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {TRAINING_NEED_AREAS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={n.priority || ""} disabled={!editable}
                      onValueChange={(v) => update((c) => ({ ...c, training_needs: c.training_needs.map((x, j) => j === i ? { ...x, priority: v } : x) }))}>
                      <SelectTrigger data-testid={`need-priority-${i}`} className="h-10"><SelectValue placeholder="Priority" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Description" value={n.description || ""} disabled={!editable} data-testid={`need-description-${i}`}
                      onChange={(e) => update((c) => ({ ...c, training_needs: c.training_needs.map((x, j) => j === i ? { ...x, description: e.target.value } : x) }))} />
                    <Input placeholder="Recommended strategy" value={n.strategy || ""} disabled={!editable} data-testid={`need-strategy-${i}`}
                      onChange={(e) => update((c) => ({ ...c, training_needs: c.training_needs.map((x, j) => j === i ? { ...x, strategy: e.target.value } : x) }))} />
                    <Input placeholder="Target / outcome" value={n.target || ""} disabled={!editable} data-testid={`need-target-${i}`}
                      onChange={(e) => update((c) => ({ ...c, training_needs: c.training_needs.map((x, j) => j === i ? { ...x, target: e.target.value } : x) }))} />
                    {editable && (
                      <Button type="button" variant="ghost" className="h-10 text-red-600" data-testid={`need-remove-${i}`}
                        onClick={() => update((c) => ({ ...c, training_needs: c.training_needs.filter((_, j) => j !== i) }))}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {editable && (
                  <Button type="button" variant="outline" className="h-10" data-testid="need-add-button"
                    onClick={() => update((c) => ({ ...c, training_needs: [...(c.training_needs || []), { area: "", priority: "", description: "", strategy: "", target: "" }] }))}>
                    <Plus className="h-4 w-4 mr-2" /> Add training need
                  </Button>
                )}
              </div>

              <div className="rounded-md border border-border p-4 space-y-3" data-testid="adjustments-section">
                <h3 className="font-semibold text-slate-800 text-sm">Reasonable adjustments / support plan</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ADJUSTMENT_OPTIONS.map((a) => (
                    <label key={a} className="flex items-center gap-2 text-sm min-h-[44px]">
                      <Checkbox data-testid={`adjustment-${a}`} checked={(content.adjustments.selected || []).includes(a)} disabled={!editable}
                        onCheckedChange={(v) => update((c) => ({ ...c, adjustments: { ...c.adjustments, selected: v ? [...c.adjustments.selected, a] : c.adjustments.selected.filter((x) => x !== a) } }))} />
                      {a.replace(/_/g, " ")}
                    </label>
                  ))}
                </div>
                <Textarea rows={2} placeholder="Custom adjustments (not every SEND child needs the same adjustments)" value={content.adjustments.custom || ""} disabled={!editable}
                  data-testid="adjustments-custom"
                  onChange={(e) => update((c) => ({ ...c, adjustments: { ...c.adjustments, custom: e.target.value } }))} />
              </div>

              <div className="rounded-md border border-border p-4 space-y-3" data-testid="safety-concerns-section">
                <h3 className="font-semibold text-slate-800 text-sm">Safety concerns identified</h3>
                {(content.safety_concerns || []).map((sc, i) => (
                  <div key={i} className="rounded-md border border-border p-3 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`safety-concern-${i}`}>
                    <Select value={sc.category || ""} disabled={!editable}
                      onValueChange={(v) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, category: v } : x) }))}>
                      <SelectTrigger data-testid={`concern-category-${i}`} className="h-10"><SelectValue placeholder="Category" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        {CONCERN_CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select value={sc.severity || ""} disabled={!editable}
                      onValueChange={(v) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, severity: v } : x) }))}>
                      <SelectTrigger data-testid={`concern-severity-${i}`} className="h-10"><SelectValue placeholder="Severity for review" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input placeholder="Concern" value={sc.concern || ""} disabled={!editable} data-testid={`concern-text-${i}`}
                      onChange={(e) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, concern: e.target.value } : x) }))} />
                    <Input placeholder="Evidence / observation" value={sc.evidence || ""} disabled={!editable} data-testid={`concern-evidence-${i}`}
                      onChange={(e) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, evidence: e.target.value } : x) }))} />
                    <Input placeholder="Proposed mitigation" value={sc.mitigation || ""} disabled={!editable} data-testid={`concern-mitigation-${i}`}
                      onChange={(e) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, mitigation: e.target.value } : x) }))} />
                    <div className="flex gap-2 items-center">
                      <Input placeholder="Training required" value={sc.training || ""} disabled={!editable} data-testid={`concern-training-${i}`}
                        onChange={(e) => update((c) => ({ ...c, safety_concerns: c.safety_concerns.map((x, j) => j === i ? { ...x, training: e.target.value } : x) }))} />
                      {editable && (
                        <Button type="button" variant="ghost" className="h-10 text-red-600" data-testid={`concern-remove-${i}`}
                          onClick={() => update((c) => ({ ...c, safety_concerns: c.safety_concerns.filter((_, j) => j !== i) }))}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {editable && (
                  <Button type="button" variant="outline" className="h-10" data-testid="concern-add-button"
                    onClick={() => update((c) => ({ ...c, safety_concerns: [...(c.safety_concerns || []), { category: "", severity: "", concern: "", evidence: "", mitigation: "", training: "" }] }))}>
                    <Plus className="h-4 w-4 mr-2" /> Add safety concern
                  </Button>
                )}
                <p className="text-xs text-slate-500">Concerns are flagged for professional review — they never produce an automatic pass/fail.</p>
              </div>
            </>
          )}

          {group.type === "summary" && (
            <div className="space-y-4" data-testid="summary-section">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[["key_strengths", "Key strengths *"], ["key_concerns", "Key concerns *"],
                  ["training_needs_summary", "Training needs *"], ["required_adjustments", "Required adjustments"],
                  ["risk_considerations", "Risk considerations"], ["recommended_approach", "Recommended training approach *"],
                  ["further_information", "Further information required"], ["other_recommendations", "Other professional recommendations"]].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Textarea rows={3} value={content.summary[k] || ""} disabled={!editable} data-testid={`summary-${k}`}
                      onChange={(e) => update((c) => ({ ...c, summary: { ...c.summary, [k]: e.target.value } }))} />
                  </div>
                ))}
              </div>

              <div className="rounded-md border-2 border-primary/30 bg-brand-light/40 p-4 space-y-3" data-testid="recommendation-section">
                <h3 className="font-heading font-bold text-slate-900">Assessor recommendation *</h3>
                <p className="text-xs text-slate-600">The system never generates a suitability decision from scores. Record your professional judgement.</p>
                <div className="space-y-2">
                  {RECOMMENDATIONS.map((r) => (
                    <label key={r.value} className={`flex items-center gap-3 rounded-md border p-3 text-sm min-h-[48px] transition-colors duration-150 ${
                      recommendation === r.value ? "border-primary bg-white font-semibold" : "border-border bg-white/60"
                    } ${!editable ? "opacity-80" : "cursor-pointer hover:bg-white"}`} data-testid={`recommendation-${r.value}`}>
                      <input type="radio" name="recommendation" className="h-4 w-4 accent-[#005EB8]" disabled={!editable}
                        checked={recommendation === r.value}
                        onChange={() => { setRecommendation(r.value); setDirty(true); }} />
                      {r.label}
                    </label>
                  ))}
                </div>
                <div className="space-y-1.5">
                  <Label>Reason for recommendation *</Label>
                  <Textarea rows={3} value={recReason} disabled={!editable} data-testid="recommendation-reason"
                    onChange={(e) => { setRecReason(e.target.value); setDirty(true); }} />
                </div>
              </div>

              {editable && (
                <Button onClick={submit} disabled={submitting} data-testid="submit-assessment-button"
                  className="w-full h-12 bg-ukgreen hover:bg-green-800 text-white font-bold text-base">
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="h-5 w-5 mr-2" /> Submit for manager review</>}
                </Button>
              )}

              {canReview && (
                <div className="rounded-md border-2 border-indigo-300 bg-indigo-50 p-4 space-y-3" data-testid="manager-review-panel">
                  <h3 className="font-heading font-bold text-slate-900">Manager review</h3>
                  <p className="text-xs text-slate-600">Review the complete assessment, flagged concerns, strengths, training needs and recommendation. You cannot edit the assessor's submitted content — use "Return for amendment" if changes are needed.</p>
                  <div className="space-y-1.5">
                    <Label>Decision</Label>
                    <Select value={reviewDecision} onValueChange={setReviewDecision}>
                      <SelectTrigger data-testid="review-decision-select" className="h-11 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="approve" data-testid="review-approve">Approve</SelectItem>
                        <SelectItem value="provisional_approval" data-testid="review-provisional">Provisional approval — training may start (assessment week)</SelectItem>
                        <SelectItem value="return_for_amendment" data-testid="review-return">Return for amendment</SelectItem>
                        <SelectItem value="request_further_information" data-testid="review-info">Request further information</SelectItem>
                        <SelectItem value="request_further_assessment" data-testid="review-further">Request further assessment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Comments {!["approve", "provisional_approval"].includes(reviewDecision) && "*"}</Label>
                    <Textarea rows={2} value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} data-testid="review-comments" />
                  </div>
                  <Button onClick={review} data-testid="review-submit-button" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
                    Record decision
                  </Button>
                </div>
              )}

              {assessment.status === "provisional" && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="provisional-banner">
                  Provisionally approved{assessment.provisional_by_name ? ` by ${assessment.provisional_by_name}` : ""} — the assigned trainer
                  completes this assessment during the on-road assessment week, then submits it for final approval.
                </div>
              )}

              {canProvisional && !canReview && (
                <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="provisional-panel">
                  <h3 className="font-heading font-bold text-slate-900">Provisional approval — home visit</h3>
                  <p className="text-xs text-slate-600">
                    Record the home-visit go-ahead so training can start (on-road assessment week). The assigned trainer
                    completes this assessment during the week and submits it for final approval. If it is not approved
                    within 5 sessions or 7 days, the programme is flagged "Assessment overdue" and you are alerted.
                  </p>
                  <div className="space-y-1.5">
                    <Label>Comments (optional)</Label>
                    <Textarea rows={2} value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} data-testid="provisional-comments" />
                  </div>
                  <Button onClick={() => review("provisional_approval")} data-testid="provisional-approve-button"
                    className="h-11 bg-amber-600 hover:bg-amber-700 text-white font-semibold">
                    Record provisional approval — training may start
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="outline" className="h-11" data-testid="section-prev-button" disabled={groupIdx === 0} onClick={() => setGroupIdx((i) => i - 1)}>
          <ArrowLeft className="h-4 w-4 mr-2" /> Previous
        </Button>
        <span className="text-xs text-slate-500" data-testid="section-progress">Section {group.num} of {GROUPS.length - 1}</span>
        <Button variant="outline" className="h-11" data-testid="section-next-button" disabled={groupIdx === GROUPS.length - 1} onClick={() => setGroupIdx((i) => i + 1)}>
          Next <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

function readOnlyGuard(editable) {
  return !editable;
}
